import { useEffect, useRef, useSyncExternalStore } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { evaluateReadiness, type ReadinessCheck } from '../../interaction/camera';
import { HERO_1_PLAYER_ID, HERO_2_PLAYER_ID } from '../../interaction/keyboard';
import { INVENTED_CALIBRATION_COPY } from '../../platform/config/calibration';
import { INVENTED_CAMERA_SETUP_COPY } from '../../platform/config/camera-mode';
import {
  INVENTED_COOPERATIVE_READINESS_COPY,
  INVENTED_READINESS_COPY,
  READINESS_MESSAGES,
} from '../../platform/config/camera-readiness';
import { INVENTED_COOPERATIVE_CALIBRATION_COPY } from '../../platform/config/cooperative-calibration';
import type { CameraMode } from '../../shared/types/camera-mode';
import { useAppServices } from '../shell/services-context';

/** GDS §12's own "…Mode Ready" wording, which is also how a mode is named to a grown-up. */
const MODE_READY_MESSAGE: Readonly<Record<CameraMode, string>> = {
  compact: READINESS_MESSAGES.compactReady,
  'full-motion': READINESS_MESSAGES.fullMotionReady,
};

/**
 * Hardware readiness (CAM-01, GDS §12) — React-owned, because camera readiness is shell work
 * (architecture §6), not gameplay.
 *
 * What this screen is allowed to know is deliberately thin: it renders a `CameraSessionSnapshot`,
 * which is scalars and statuses. It never calls `getUserMedia`, never sees a frame and cannot name
 * a landmark — the session owns the stream and the pose boundary owns everything else
 * (architecture §11, §23, §25).
 *
 * The video element is the *only* place a frame is visible, and it is mirrored, because "left and
 * right game zones must visually match the child's movement" (MVP notes §5). It carries `muted`
 * and no recording of any kind: privacy checklist rows 2 and 3.
 *
 * Copy discipline: every string on this screen comes from the config layer, either verbatim from
 * GDS §12 or from the declared invented set (D-049(5) pattern). Nothing is written inline.
 */
export function CameraCheckPage() {
  const { camera, sessionLaunchConfig, setCameraInputEnabled } = useAppServices();
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);

  const snapshot = useSyncExternalStore(camera.subscribe, camera.getSnapshot, camera.getSnapshot);

  /**
   * Whether two heroes are setting up together (Slice A′, CAM-07).
   *
   * Read from the sitting a grown-up configured, which is the only thing that knows — and it is the
   * *same* holder the stage reads, so there is one answer to "who is playing" in the app rather than
   * two that can disagree. Session-only: a reload has no sitting, and this screen is honestly
   * single-player again.
   */
  const cooperative = sessionLaunchConfig?.playerMode === 'COOP_SIMULTANEOUS_SIMPLE';

  /**
   * Whether both heroes have been to hero training in this sitting (Slice D′).
   *
   * The two fixed lane ids are what the cooperative trainer saves under (D-080(3)), and this asks
   * the same session-only store the stage will ask. It is deliberately the *training* question and
   * not the stage's full architecture §20 reuse question: this screen says whether the children have
   * done their training, which is a thing a grown-up can act on, while the stage remains the one
   * place that decides whether a profile may actually serve a mission — and says so in its own
   * readout if it may not.
   */
  const heroesTrained = [HERO_1_PLAYER_ID, HERO_2_PLAYER_ID].filter(
    (playerId) => camera.calibration.get(playerId) !== null,
  ).length;
  const bothHeroesTrained = heroesTrained === 2;
  /**
   * Whether **any** hero can be read by the camera (Slice F′, D-076(5)).
   *
   * The entry guard became per child, so one trained hero is now a genuinely playable camera
   * sitting: that hero answers with their body and their sibling answers with their keys, in the
   * same mission. Offering only the keyboard mission here would have walked such a family past a
   * camera the stage was perfectly willing to use.
   */
  const anyHeroTrained = heroesTrained > 0;

  useEffect(() => {
    // Two children need two bodies looked for, and a solo child must not pay for a second one that
    // nobody is looking for (D-077(1)): asked before the camera starts, so the engine is built once.
    camera.requestMaxPoses(cooperative ? 2 : 1);
    camera.attach(videoRef.current);
    void camera.start();

    // Leaving the screen ends the camera (architecture §23; PRV-02). The engine stays warm — this
    // is a child walking away from a checklist, not the end of the sitting.
    return () => {
      camera.stop();
      camera.attach(null);
    };
  }, [camera, cooperative]);

  const report = evaluateReadiness(snapshot.readings, { cooperative });
  const blockedCheck = report.checks.find((check) => check.status === 'blocked') ?? null;

  /**
   * The readiness half of D-059: measure, then *recommend*.
   *
   * Recorded on the sitting rather than raised as a route parameter or a stored preference, because
   * the parent setup screen is a whole navigation away and this is not a fact about the child.
   * Nothing here decides anything — Compact is GDS §15's accessibility path, and an accessibility
   * decision belongs to a grown-up rather than to a distance heuristic that a lighting blip could
   * flip mid-sentence.
   */
  const recommended = report.recommendedMode;
  useEffect(() => {
    camera.recommendCameraMode(recommended);
  }, [camera, recommended]);
  /**
   * A cooperative sitting shows only what it measured, and never a remembered Compact.
   *
   * The last-measured fallback exists so a grown-up walking to the setup screen with the child in
   * tow still sees a suggestion (D-066(1)); in a two-hero sitting that same fallback could put
   * "Compact Mode Ready" on screen from an earlier solo check, which is the one thing D-076(4) says
   * cooperative play may not have.
   */
  const shownRecommendation = cooperative
    ? recommended
    : (recommended ?? camera.recommendedCameraMode);

  return (
    <section data-testid="camera-check">
      <h1 data-testid="readiness-copy">{INVENTED_READINESS_COPY.pageTitle}</h1>
      <p className="lede" data-testid="readiness-copy">
        {INVENTED_READINESS_COPY.pageLede}
      </p>

      <div className="camera-check__stage">
        <video
          ref={videoRef}
          className="camera-check__feed"
          data-testid="camera-feed"
          data-mirrored="true"
          autoPlay
          playsInline
          muted
        />
        {snapshot.streamActive ? null : <div className="camera-check__feed-placeholder" />}
      </div>

      <p
        className="camera-check__headline"
        data-testid="readiness-headline"
        role="status"
        aria-live="polite"
      >
        <span data-testid="readiness-copy">{report.headline}</span>
      </p>

      {blockedCheck === null ? null : (
        <div className="panel" data-testid="camera-blocked">
          <h2 data-testid="readiness-copy">{blockedCheck.message}</h2>
          <p data-testid="readiness-copy">{guidanceFor(blockedCheck)}</p>
          <p>
            <button
              type="button"
              className="button button--primary"
              onClick={() => {
                void camera.start();
              }}
            >
              <span data-testid="readiness-copy">{INVENTED_READINESS_COPY.retry}</span>
            </button>
          </p>
        </div>
      )}

      {/*
       * What this room looks like it suits (GDS §6's closing line, D-059).
       *
       * Grown-up-facing rather than child-facing: it names a setting a parent will meet again on
       * the setup screen, and it is careful to *suggest*. It says nothing about the child — a room
       * is small or a camera is close, and neither is a fact about them. The mode names themselves
       * are GDS §12's own "…Mode Ready" messages, so nothing new is invented for them.
       */}
      {shownRecommendation === null ? null : (
        <p
          className="notice"
          data-testid="readiness-mode-recommendation"
          data-recommended-mode={shownRecommendation}
        >
          <strong>{INVENTED_CAMERA_SETUP_COPY.readinessRecommendation}:</strong>{' '}
          {MODE_READY_MESSAGE[shownRecommendation]}
        </p>
      )}

      {/*
       * The two-hero notes, for the grown-up setting the room up (Slice A′, retired and replaced
       * by Slice D′'s conditional truth).
       *
       * Two facts never change: cooperative play needs the Full-Motion space (D-076(4)), and a room
       * that cannot fit two lanes has the keyboard rather than a smaller play space. The third one
       * depends on the sitting — whether both heroes have trained — and it is now read from the
       * sitting rather than asserted, because the cooperative camera mission exists.
       */}
      {!cooperative ? null : (
        <div className="panel" data-testid="cooperative-readiness-note">
          <p className="notice">{INVENTED_COOPERATIVE_READINESS_COPY.fullMotionOnly}</p>
          <p className="notice">{INVENTED_COOPERATIVE_READINESS_COPY.keyboardInstead}</p>
          {/*
            Three states since Slice F′, each true on its own terms: nobody trained (the mission
            runs on two sets of keys), one hero trained (a mixed sitting, which the per-child entry
            guard makes real), and both trained. D′'s two branches were true only while the guard
            was all-or-nothing.
          */}
          <h2 style={{ marginBottom: 0 }}>
            {bothHeroesTrained
              ? INVENTED_COOPERATIVE_READINESS_COPY.cameraReadyTitle
              : anyHeroTrained
                ? INVENTED_COOPERATIVE_READINESS_COPY.oneHeroTitle
                : INVENTED_COOPERATIVE_READINESS_COPY.trainBothTitle}
          </h2>
          <p className="lede">
            {bothHeroesTrained
              ? INVENTED_COOPERATIVE_READINESS_COPY.cameraReadyBody
              : anyHeroTrained
                ? INVENTED_COOPERATIVE_READINESS_COPY.oneHeroBody
                : INVENTED_COOPERATIVE_READINESS_COPY.trainBothBody}
          </p>
        </div>
      )}

      <ul className="readiness" data-testid="readiness-checklist">
        {report.checks.map((check) => (
          <li
            key={check.id}
            data-testid="readiness-check"
            data-check-id={check.id}
            data-status={check.status}
          >
            <span className="readiness__label" data-testid="readiness-copy">
              {check.label}
            </span>
            {check.message === null ? null : (
              <span className="readiness__message" data-testid="readiness-copy">
                {check.message}
              </span>
            )}
          </li>
        ))}
      </ul>

      <p>
        {/*
          Hero training is offered whenever the camera itself is working — that is, whenever
          nothing is *blocked*. A soft "attention" state (a dim room, a child standing a little
          too close) is guidance the child fixes while they are training, and the training screen
          repeats it; refusing to let them start would strand a child in front of a working camera
          because the light was not perfect. Blocking states offer the keyboard instead.
        */}
        {report.blocked || cooperative ? null : (
          <Link to="/calibration" className="button button--primary">
            <span data-testid="calibration-copy">{INVENTED_CALIBRATION_COPY.start}</span>
          </Link>
        )}
        {/*
         * Two-hero training (Slice C′).
         *
         * The *solo* trainer stays deliberately absent for two heroes: GDS §13's seven steps
         * measure one body in the middle of the picture, and sending a cooperative sitting through
         * it would leave the second child unread with a frame-wide set of portals. This is the
         * two-hero one — Hero 1, then Hero 2, then the check they do together — and it needs the
         * sitting the grown-up set up, so it is offered only where that sitting is known.
         */}
        {!cooperative || gameId === undefined || report.blocked ? null : (
          <Link to={`/play/${gameId}/coop-calibration`} className="button button--primary">
            <span data-testid="calibration-copy">
              {INVENTED_COOPERATIVE_CALIBRATION_COPY.start}
            </span>
          </Link>
        )}
        {/*
         * Starting the sitting this screen was reached from (Slice A′), and — since Slice D′ —
         * starting it *with the camera* when the sitting can genuinely be played that way.
         *
         * A button rather than a link in that one case because it does something first: the same
         * `setCameraInputEnabled(true)` the solo trainer's own handoff performs (Slice D). Without
         * it a family who trained both heroes would be walked into a keyboard mission by a control
         * that had just told them the camera was ready, which is the kind of quiet untruth this
         * slice exists to remove.
         */}
        {gameId === undefined ? null : cooperative && anyHeroTrained ? (
          <button
            type="button"
            className="button button--primary"
            data-testid="camera-check-start-with-camera"
            onClick={() => {
              setCameraInputEnabled(true);
              void navigate(`/play/${gameId}`);
            }}
          >
            <span data-testid="readiness-copy">
              {INVENTED_COOPERATIVE_READINESS_COPY.startMissionWithCamera}
            </span>
          </button>
        ) : (
          <Link to={`/play/${gameId}`} className="button button--primary">
            <span data-testid="readiness-copy">
              {INVENTED_COOPERATIVE_READINESS_COPY.startMission}
            </span>
          </Link>
        )}
        <Link to="/" className="button">
          <span data-testid="readiness-copy">{INVENTED_READINESS_COPY.back}</span>
        </Link>
      </p>
    </section>
  );
}

/**
 * The longer explanation that goes with a blocking state. Config-layer copy only.
 *
 * Keyed off the headline rather than the row, because the permission row carries two different
 * situations: a grown-up who said no, and a camera that is not there at all.
 */
function guidanceFor(check: ReadinessCheck): string {
  if (check.message === INVENTED_READINESS_COPY.permissionDeniedTitle) {
    return INVENTED_READINESS_COPY.permissionDeniedBody;
  }
  if (check.message === INVENTED_READINESS_COPY.unsupportedBrowserTitle) {
    return INVENTED_READINESS_COPY.unsupportedBrowserBody;
  }
  if (check.message === INVENTED_READINESS_COPY.poseUnavailableTitle) {
    return INVENTED_READINESS_COPY.poseUnavailableBody;
  }
  return INVENTED_READINESS_COPY.noCameraBody;
}
